"use client"

import { DropPreviewType } from "@anaconda/core/drop/types"
import { Trans, useLingui } from "@lingui/react/macro"
import { cn } from "@rarible_int/anaconda_ui_components"
import Image from "next/image"
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react"

// Previews show from Tailwind's lg breakpoint up. Below it they are
// display:none, which does not stop a video from downloading.
const LARGE_SCREEN_QUERY = "(min-width: 1024px)"

/**
 * How long each still of a `mediaSequence` shows: the configured slides'
 * square preview videos show three PFPs in 3.13 s.
 */
export const SEQUENCE_FRAME_MS = 1_050

function subscribeToLargeScreen(onChange: () => void) {
  const mql = window.matchMedia(LARGE_SCREEN_QUERY)
  mql.addEventListener("change", onChange)
  return () => mql.removeEventListener("change", onChange)
}

interface Props {
  slideActive: boolean
  withTimer: boolean
  timerDurationInSeconds: number
  mediaSrc: string
  mediaType?: DropPreviewType
  /** Still for a video preview; without one the video preloads its first frame */
  posterSrc?: string
  /**
   * Stills to cut between while the slide is active, in place of a video.
   * The first one shows otherwise, as a video shows its first frame.
   */
  mediaSequence?: string[]
  title: string
  onClick: () => void
}

export function DesktopSlidePreview({
  slideActive,
  withTimer,
  timerDurationInSeconds,
  mediaSrc,
  mediaType,
  posterSrc,
  mediaSequence,
  title,
  onClick,
}: Props) {
  const { t } = useLingui()
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      onClick()
    },
    [onClick],
  )

  const videoRef = useRef<HTMLVideoElement>(null)
  const visible = useSyncExternalStore(
    subscribeToLargeScreen,
    () => window.matchMedia(LARGE_SCREEN_QUERY).matches,
    () => false,
  )
  const playing = visible && slideActive

  const frames = mediaSequence && mediaSequence.length > 1 ? mediaSequence : []
  const [frame, setFrame] = useState(0)
  // The sequence plays when a video would: on the active, visible preview
  useEffect(() => {
    setFrame(0)
    if (!playing || frames.length === 0) return
    const timer = setInterval(
      () => setFrame((current) => (current + 1) % frames.length),
      SEQUENCE_FRAME_MS,
    )
    return () => clearInterval(timer)
  }, [playing, frames.length])

  // Only the active preview plays; a looping video downloads in full.
  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    if (playing) {
      video.play().catch(() => {
        // Ignore autoplay errors
      })
    } else {
      video.pause()
    }
  }, [playing])

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        "relative transition-all duration-300",
        slideActive
          ? "h-[120px] w-[120px] rounded-lg border-2 border-white/20 p-1"
          : "h-16 w-16 rounded opacity-60 hover:opacity-100",
      )}
    >
      {withTimer && slideActive && (
        <Timer
          durationInSeconds={timerDurationInSeconds}
          className="absolute inset-0 translate-x-[-2px] translate-y-[-2px]"
        />
      )}
      <div className="relative h-full w-full overflow-hidden rounded">
        {frames.length > 0 ? (
          // Hard cuts, like the preview videos; every still stays mounted so
          // the next one is loaded before it shows
          frames.map((src, index) => (
            <Image
              key={src}
              className={cn("object-cover", index !== frame && "invisible")}
              src={src}
              fill
              alt={`${title} ${t`preview`} ${index + 1}`}
              unoptimized
            />
          ))
        ) : mediaType === DropPreviewType.Video ? (
          <video
            playsInline
            ref={videoRef}
            className="object-cover"
            src={mediaSrc}
            poster={visible ? posterSrc : undefined}
            muted
            loop
            preload={visible && !posterSrc ? "metadata" : "none"}
          />
        ) : (
          <Image
            className="object-cover"
            src={mediaSrc}
            fill
            alt={`${title} ${t`preview`}`}
            unoptimized
          />
        )}
      </div>
    </button>
  )
}

function Timer({
  size = 120,
  durationInSeconds = 5,
  strokeWidth = 2,
  className,
}: {
  size?: number
  strokeWidth?: number
  durationInSeconds?: number
  className?: string
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>
        <Trans>Slide timer</Trans>
      </title>
      <rect
        width={size - strokeWidth}
        height={size - strokeWidth}
        x={strokeWidth / 2}
        y={strokeWidth / 2}
        strokeWidth={strokeWidth}
        rx="8"
        ry="8"
        fill="transparent"
        stroke="white"
        strokeDasharray="400%"
        style={{ animationDuration: `${durationInSeconds}s` }}
        className="animate-[square-stroke-fill_linear_forwards]"
      />
    </svg>
  )
}
